import React, { useEffect, useState } from 'react';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import {
    Box, Grid, Paper, Typography, Button, IconButton,
    Tooltip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { ValidatorForm } from 'react-material-ui-form-validator';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import axios from '../../../axios-order';
import moment from 'moment';

const supplyTypeOptions = [
    { id: 1, supply_type: "Outward" },
    { id: 2, supply_type: "Inward" }
];

const documentTypeOptions = [
    { id: 1, document_type: "Tax Invoice" },
    { id: 2, document_type: "Bill of Supply" },
    { id: 3, document_type: "Delivery Challan" },
    { id: 4, document_type: "Credit Note" },
    { id: 5, document_type: "Debit Note" },
    { id: 6, document_type: "Others" },
];

const transactionTypeOptions = [
    { id: 1, transaction_type: "Regular" },
    { id: 2, transaction_type: "Bill From - Dispatch From" },
    { id: 3, transaction_type: "Bill To - Ship To" },
    { id: 4, transaction_type: "Combination 2 & 3" },
];

const gstRateOptions = [
    { id: 1, gst_rate: "0%" },
    { id: 2, gst_rate: "0.1%" },
    { id: 3, gst_rate: "0.25%" },
    { id: 4, gst_rate: "1.5%" },
    { id: 5, gst_rate: "3%" },
    { id: 6, gst_rate: "5%" },
    { id: 7, gst_rate: "7.5%" },
    { id: 8, gst_rate: "12%" },
    { id: 9, gst_rate: "18%" },
    { id: 10, gst_rate: "28%" },
];

const cessAdvRateOptions = [
    { id: 1, cess_adv_rate: "-Select-" },
    { id: 2, cess_adv_rate: "1%" },
    { id: 3, cess_adv_rate: "5%" },
];

const subTypeRadioOptions = [
    'Supply', 'Job Work', 'SKD/CKD/Lots', 'Recipient Not Known',
    'For Own Use', 'Exhibition or Fairs', 'Line Sales', 'Others'
];

const stateList = [
    "Jammu & Kashmir", "Himachal Pradesh", "Punjab", "Chandigarh", "Uttarakhand",
    "Haryana", "Delhi", "Rajasthan", "Uttar Pradesh", "Bihar", "Sikkim",
    "Arunachal Pradesh", "Nagaland", "Manipur", "Mizoram", "Tripura", "Meghalaya",
    "Assam", "West Bengal", "Jharkhand", "Odisha", "Chhattisgarh", "Madhya Pradesh",
    "Gujarat", "Daman & Diu", "Dadra & Nagar Haveli", "Maharashtra", "Karnataka",
    "Goa", "Lakshadweep", "Kerala", "Tamil Nadu", "Puducherry",
    "Andaman & Nicobar Islands", "Telangana", "Andhra Pradesh", "Ladakh",
    "Other Territory", "Other Country"
];

// ─── Styles ───────────────────────────────────────────────────────────────────
const sectionHeaderStyle = {
    backgroundColor: '#d3dae0',
    padding: '13px 16px',
    fontWeight: 700,
    fontSize: '1.15rem',
    color: '#2c3e50',
    borderLeft: '4px solid #607d8b',
    marginBottom: '8px',
    letterSpacing: '0.3px',
    fontFamily: "Dosis"
};

const labelStyle = {
    fontSize: '1.1rem',
    color: '#444',
    fontWeight: 600,
    marginBottom: '6px',
    display: 'block',
    fontFamily: "Dosis"
};

const requiredStar = <span style={{ color: 'red' }}>*</span>;
const fieldWrapper = { padding: '8px 12px' };

const tableCellHeaderStyle = {
    backgroundColor: '#607d8b',
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.95rem',
    padding: '12px 8px',
    textAlign: 'center',
    border: '1px solid #d0d0d0',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    fontFamily: "Dosis"
};

const tableCellStyle = {
    padding: '10px 8px',
    fontSize: '0.95rem',
    fontWeight: 500,
    border: '1px solid #d0d0d0',
    textAlign: 'center',
    wordBreak: 'break-word',
    fontFamily: "Dosis"
};

const inputSmallStyle = {
    width: '100%',
    padding: '9px 13px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1rem',
    height: '46px',
    outline: 'none',
    boxSizing: 'border-box',
    backgroundColor: '#f5f5f5',
    color: '#333',
    fontFamily: "Dosis"
};

const selectSmallStyle = {
    ...inputSmallStyle,
    backgroundColor: '#f5f5f5',
};

const summaryRowStyle = {
    backgroundColor: '#e8edf2',
    fontWeight: 700,
    fontSize: '1rem',
    padding: '14px 10px',
    textAlign: 'center',
    borderTop: '2px solid #607d8b',
    verticalAlign: 'middle',
    fontFamily: "Dosis"
};

// ─── Main Component ───────────────────────────────────────────────────────────
const EWayBill = () => {
    const location = useLocation();
    const history = useHistory();
    const { id } = useParams();

    const handleBack = () => {
        history.push('/einvoice/tax-invoice');
    };

    const tax_master_id = location.state?.tax_master_id || id;
    const bid = localStorage.getItem('business_entity_id');

    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [responseMessage, setResponseMessage] = useState('');
    const [ewayBillExists, setEwayBillExists] = useState(false);

    const [transactionDetails, setTransactionDetails] = useState({
        supply_type: 'Outward',
        sub_type: 'Supply',
        document_type: '',
        document_no: '',
        document_date: '',
        transaction_type: 'Regular',
    });

    const [billFrom, setBillFrom] = useState({ name: '', gstin: '', state: '' });
    const [dispatchFrom, setDispatchFrom] = useState({ address: '', place: '', pincode: '', state: '' });
    const [billTo, setBillTo] = useState({ name: '', gstin: '', state: '' });
    const [shipTo, setShipTo] = useState({ address: '', place: '', pincode: '', state: '' });

    const [items, setItems] = useState([{
        id: 1, product_name: '', description: '', hsn: '', quantity: '',
        unit: '', value_taxable_value: '', cgst_sgst_rate: '',
        igst_rate: '', cess_adv_rate: '', cess_non_adv_rate: '',
    }]);

    const [totals, setTotals] = useState({
        total_taxable_amount: '', cgst_amount: '', sgst_amount: '',
        igst_amount: '', cess_adv_amount: '', cess_non_adv_amount: '',
        other_amount: '', total_inv_amount: '',
    });

    // ─── Part-B State ─────────────────────────────────────────────────────────
    const [transportMode, setTransportMode] = useState('Road');
    const [vehicleType, setVehicleType] = useState('Regular');
    const [vehicleNo, setVehicleNo] = useState('');
    const [transporterDocNo, setTransporterDocNo] = useState('');
    const [transporterDocDate, setTransporterDocDate] = useState('');

    // ─── Transportation Details State ─────────────────────────────────────────
    const [transporterIdError, setTransporterIdError] = useState('');
    const [transporterNameError, setTransporterNameError] = useState('');
    const [approximateDistanceError, setApproximateDistanceError] = useState('');
    const [transporterId, setTransporterId] = useState('');
    const [transporterName, setTransporterName] = useState('');
    const [autoCalculatedDistance, setAutoCalculatedDistance] = useState('');
    const [autoCalculatedDistanceError, setAutoCalculatedDistanceError] = useState('');
    const [approximateDistance, setApproximateDistance] = useState('');

    // ─── Preview State ────────────────────────────────────────────────────────
    const [previewOpen, setPreviewOpen] = useState(false);

    const populateForm = (data) => {
        setBillFrom({
            name: data.bill_from?.name || '',
            gstin: data.bill_from?.gstin || '',
            state: data.bill_from?.state || ''
        });

        setDispatchFrom({
            address: data.bill_from?.address || '',
            place: data.bill_from?.location || '',
            pincode: data.bill_from?.pincode || '',
            state: data.bill_from?.state || ''
        });

        setBillTo({
            name: data.bill_to?.name || '',
            gstin: data.bill_to?.gstin || '',
            state: data.bill_to?.state || ''
        });

        setShipTo({
            address: data.bill_to?.address || '',
            place: data.bill_to?.location || '',
            pincode: data.bill_to?.pincode || '',
            state: data.bill_to?.state || ''
        });

        const mapDocumentType = (docType) => {
            const mapping = {
                'Invoice': 'Tax Invoice',
                'Tax Invoice': 'Tax Invoice',
                'Credit Note': 'Credit Note',
                'Debit Note': 'Debit Note',
                'Bill': 'Bill of Supply',
                'Delivery Challan': 'Delivery Challan',
            };
            return mapping[docType] || docType || '';
        };

        const mapGstRate = (rate) => {
            if (rate == null) return '';
            const num = parseFloat(rate);
            const match = gstRateOptions.find(o => parseFloat(o.gst_rate) === num);
            return match ? match.gst_rate : '';
        };

        const parseFormatted = (val) => {
            if (val == null) return 0;
            if (typeof val === 'number') return val;
            return parseFloat(String(val).replace(/,/g, '')) || 0;
        };

        setTransactionDetails(prev => ({
            ...prev,
            supply_type: 'Outward',
            sub_type: 'Supply',
            document_type: mapDocumentType(data.inv_det_document_type),
            document_date: data.invoice_date
                ? moment(data.invoice_date, 'DD/MM/YYYY').format('YYYY-MM-DD')
                : '',
            document_no: data.invoice_number || '',
            transaction_type: 'Regular',
        }));

        if (data.items && data.items.length > 0) {
            const mappedItems = data.items.map((item, idx) => ({
                id: idx + 1,
                product_name: item.description || '',
                description: item.line_item_description || '',
                hsn: item.hsn_sac || '',
                quantity: item.qty != null ? String(item.qty) : '',
                unit: item.unit || '',
                value_taxable_value: String(parseFormatted(item.amount)),
                cgst_sgst_rate: (item.cgst > 0 || item.sgst > 0) ? mapGstRate(item.gst_rate) : '',
                igst_rate: item.igst > 0 ? mapGstRate(item.gst_rate) : '',
                cess_adv_rate: item.cess_adv_rate != null ? String(item.cess_adv_rate) : '',
                cess_non_adv_rate: item.cess_non_adv_rate != null ? String(item.cess_non_adv_rate) : '',
            }));
            setItems(mappedItems);

            setTotals({
                total_taxable_amount: String(parseFormatted(data.total_taxable).toFixed(2)),
                cgst_amount: String(parseFormatted(data.cgst_total).toFixed(2)),
                sgst_amount: String(parseFormatted(data.sgst_total).toFixed(2)),
                igst_amount: String(parseFormatted(data.igst_total).toFixed(2)),
                cess_adv_amount: String(parseFormatted(data.cess_total).toFixed(2)),
                cess_non_adv_amount: '0.00',
                other_amount: String(parseFormatted(data.other_charges)),
                total_inv_amount: String(parseFormatted(data.total).toFixed(2))
            });
        }

        // ✅ ONLY CHANGE — moved outside items block, always runs
        if (data.auto_calculated_distance && data.auto_calculated_distance !== "0") {
            setAutoCalculatedDistance(data.auto_calculated_distance);
        }

    };

    useEffect(() => {
        if (!bid || !tax_master_id) {
            setLoading(false);
            return;
        }

        axios.get(`/ewaybill/get_eway_bill/${bid}/${tax_master_id}`)
            .then((res) => {
                if (res?.data?.status && res?.data?.records) {
                    const rec = res.data.records;
                    setEwayBillExists(true);

                    setTransactionDetails(prev => ({
                        ...prev,
                        supply_type: rec.supply_type || 'Outward',
                        sub_type: rec.sub_supply_type || 'Supply',
                    }));

                    setTransporterId(rec.transporter_id || '');
                    setTransporterName(rec.transporter_name || '');
                    setAutoCalculatedDistance(rec.auto_calculated_pin_to_pin || '');
                    setApproximateDistance(rec.approximate_distance || '');

                    setTransportMode(rec.transport_mode || 'Road');
                    setVehicleType(rec.vehicle_type || 'Regular');
                    setVehicleNo(rec.vehicle_number || '');
                    setTransporterDocNo(rec.transport_doc_number || '');

                    if (rec.transport_date) {
                        setTransporterDocDate(
                            moment(rec.transport_date, 'YYYY-MM-DD').format('DD/MM/YYYY')
                        );
                    }
                }
            })
            .catch(() => { });

        // axios.get(`/einvoice/export_normal_invoice/${bid}/${tax_master_id}`)
        axios.get(`/einvoice/get_eway_bill_invoice_data/${bid}/${tax_master_id}`)
            .then((res) => {
                if (res?.data) {
                    populateForm(res.data);
                }
            })
            .catch((err) => console.error('Error fetching invoice data:', err))
            .finally(() => setLoading(false));
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();

        // ── Transporter field validation ───────────────────────────────────
        let hasError = false;
        const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

        if (!transporterId) {
            setTransporterIdError('Transporter ID is required');
            hasError = true;
        } else if (!gstinRegex.test(transporterId)) {
            setTransporterIdError('Invalid GSTIN format (e.g. 29ABCDE1234F1Z5)');
            hasError = true;
        } else {
            setTransporterIdError('');
        }

        if (!transporterName.trim()) {
            setTransporterNameError('Transporter Name is required');
            hasError = true;
        } else {
            setTransporterNameError('');
        }

        if (!autoCalculatedDistance) {
            setAutoCalculatedDistanceError('Auto Calculated Distance is required');
            hasError = true;
        } else {
            setAutoCalculatedDistanceError('');
        }

        if (!approximateDistance) {
        setApproximateDistanceError('Approximate Distance is required');
        hasError = true;
    } else {
        setApproximateDistanceError('');
    }
        if (hasError) return;   // ← stops the API call entirely
        // ── End validation ─────────────────────────────────────────────────

        setSubmitLoading(true);



        const payload = {
            // ── Identity ───────────────────────────────────────────────
            business_entity_id: bid,
            tax_master_id: tax_master_id,
            document_number: transactionDetails.document_no,

            // ── Transaction Details ────────────────────────────────────
            supplyType: transactionDetails.supply_type,
            subSupplyType: transactionDetails.sub_type,
            subSupplyDesc: "",
            docType: transactionDetails.document_type,
            docDate: transactionDetails.document_date
                ? moment(transactionDetails.document_date).format('DD/MM/YYYY')
                : '',
            transactionType: transactionDetails.transaction_type,

            // ── Bill From ──────────────────────────────────────────────
            fromGstin: billFrom.gstin,
            fromTrdName: billFrom.name,
            fromAddr1: dispatchFrom.address,
            fromAddr2: "",
            fromPlace: dispatchFrom.place,
            fromPincode: dispatchFrom.pincode,

            // ── Bill To ────────────────────────────────────────────────
            toGstin: billTo.gstin,
            toTrdName: billTo.name,
            toAddr1: shipTo.address,
            toAddr2: "",
            toPlace: shipTo.place,
            toPincode: shipTo.pincode,

            // ── Totals ─────────────────────────────────────────────────
            totalValue: parseFloat(totals.total_taxable_amount) || 0,
            cgstValue: parseFloat(totals.cgst_amount) || 0,
            sgstValue: parseFloat(totals.sgst_amount) || 0,
            igstValue: parseFloat(totals.igst_amount) || 0,
            cessValue: parseFloat(totals.cess_adv_amount) || 0,
            cessNonAdvolValue: parseFloat(totals.cess_non_adv_amount) || 0,
            otherValue: parseFloat(totals.other_amount) || 0,
            totInvValue: parseFloat(totals.total_inv_amount) || 0,

            // ── Transportation Details ─────────────────────────────────
            transporterId: transporterId,
            transporterName: transporterName,
            auto_calculated_pin_to_pin: autoCalculatedDistance,
            transDistance: approximateDistance,

            // ── Part B ─────────────────────────────────────────────────
            transMode: transportMode,
            vehicleType: vehicleType,
            vehicleNo: vehicleNo,
            transDocNo: transporterDocNo,
            transDocDate: transporterDocDate
                ? moment(transporterDocDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
                : '',

            // ── Items ──────────────────────────────────────────────────
            itemList: items.map(item => ({
                productName: item.product_name,
                productDesc: item.description,
                hsnCode: item.hsn,
                quantity: parseFloat(item.quantity) || 0,
                qtyUnit: item.unit,
                taxableAmount: parseFloat(item.value_taxable_value) || 0,
                cgstRate: parseFloat(item.cgst_sgst_rate) || 0,
                sgstRate: parseFloat(item.cgst_sgst_rate) || 0,
                igstRate: parseFloat(item.igst_rate) || 0,
                cessRate: parseFloat(item.cess_adv_rate) || 0,
                cessNonadvol: parseFloat(item.cess_non_adv_rate) || 0,
            })),
        };

        axios.post('/ewaybill/save_eway_bill', payload)
            .then((res) => {
                if (res?.data?.status) {
                    setSnackbarSeverity('success');
                    setResponseMessage(res.data.message || 'E-Way Bill saved successfully');
                    setSnackbarOpen(true);
                    setEwayBillExists(true);
                } else {
                    setSnackbarSeverity('warning');
                    setResponseMessage(res?.data?.message || 'Something went wrong');
                    setSnackbarOpen(true);
                }
            })
            .catch(() => {
                setSnackbarSeverity('error');
                setResponseMessage('E-Way Bill already exists for this invoice');
                setSnackbarOpen(true);
            })
            .finally(() => setSubmitLoading(false));
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
        setResponseMessage('');
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: '12px 16px', backgroundColor: '#f0f2f5', minHeight: '100vh', width: '100%', boxSizing: 'border-box' }}>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                <Tooltip title="Back" arrow>
                    <IconButton
                        onClick={handleBack}
                        sx={{
                            borderRadius: '50%', padding: '6px', color: '#263238',
                            backgroundColor: '#ffffff', height: '38px', width: '38px',
                            '&:hover': { backgroundColor: '#455a64', color: '#ffffff' },
                            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                            transition: 'background-color 0.3s ease',
                        }}
                    >
                        <ArrowBackIcon sx={{ fontSize: '22px' }} />
                    </IconButton>
                </Tooltip>
                <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: '#2c3e50' }}>
                    E-Way Bill
                </Typography>
            </Box>

            <ValidatorForm id="eway-bill-form" onSubmit={handleSubmit} onError={(e) => console.log(e)}>

                {/* ── TRANSACTION DETAILS ─────────────────────────────────── */}
                <Paper elevation={1} sx={{ mb: 1.5, borderRadius: '4px', overflow: 'hidden', width: '100%' }}>
                    <Box sx={sectionHeaderStyle}>Transaction Details</Box>
                    <Box sx={{ p: '8px 16px' }}>
                        <Box sx={{
                            display: 'flex', alignItems: 'flex-start', gap: 10,
                            flexWrap: 'nowrap', py: '8px', width: '100%', overflowX: 'auto'
                        }}>
                            <Box sx={{ flexShrink: 0 }}>
                                <label style={labelStyle}>Supply Type {requiredStar}</label>
                                <Box sx={{ display: 'flex', gap: 4 }}>
                                    {supplyTypeOptions.map(opt => (
                                        <label key={opt.id} style={{
                                            fontSize: '1.1rem', display: 'flex', alignItems: 'center',
                                            gap: '6px', cursor: ewayBillExists ? 'default' : 'pointer', whiteSpace: 'nowrap'
                                        }}>
                                            <input
                                                type="radio" name="supply_type" value={opt.supply_type}
                                                checked={transactionDetails.supply_type === opt.supply_type}
                                                onChange={() => !ewayBillExists && setTransactionDetails(prev => ({ ...prev, supply_type: opt.supply_type }))}
                                                disabled={ewayBillExists}
                                                style={{ accentColor: '#607d8b', width: '16px', height: '16px' }}
                                            />
                                            {opt.supply_type}
                                        </label>
                                    ))}
                                </Box>
                            </Box>
                            <Box sx={{ flexShrink: 0 }}>
                                <label style={labelStyle}>Sub Type {requiredStar}</label>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'nowrap' }}>
                                    {subTypeRadioOptions.map((v) => (
                                        <label key={v} style={{
                                            fontSize: '1.1rem', display: 'flex', alignItems: 'center',
                                            gap: '6px', cursor: ewayBillExists ? 'default' : 'pointer', whiteSpace: 'nowrap'
                                        }}>
                                            <input
                                                type="radio" name="supply_sub" value={v}
                                                checked={transactionDetails.sub_type === v}
                                                onChange={() => !ewayBillExists && setTransactionDetails(prev => ({ ...prev, sub_type: v }))}
                                                disabled={ewayBillExists}
                                                style={{ accentColor: '#607d8b', width: '16px', height: '16px' }}
                                            />
                                            {v}
                                        </label>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                        <Grid container sx={{ mt: 0.5 }}>
                            <Grid item xs={12} sm={3} sx={fieldWrapper}>
                                <label style={labelStyle}>Document Type {requiredStar}</label>
                                <input style={selectSmallStyle} value={transactionDetails.document_type} disabled />
                            </Grid>
                            <Grid item xs={12} sm={3} sx={fieldWrapper}>
                                <label style={labelStyle}>Document No {requiredStar}</label>
                                <input style={inputSmallStyle} value={transactionDetails.document_no} disabled />
                            </Grid>
                            <Grid item xs={12} sm={3} sx={fieldWrapper}>
                                <label style={labelStyle}>Document Date {requiredStar}</label>
                                <input
                                    type="date" style={inputSmallStyle}
                                    value={transactionDetails.document_date
                                        ? moment(transactionDetails.document_date).format('YYYY-MM-DD') : ''}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} sm={3} sx={fieldWrapper}>
                                <label style={labelStyle}>
                                    Transaction Type {requiredStar}
                                    <Tooltip title="Type of transaction">
                                        <InfoOutlinedIcon sx={{ fontSize: '14px', ml: 0.3, color: '#888' }} />
                                    </Tooltip>
                                </label>
                                <input style={selectSmallStyle} value={transactionDetails.transaction_type} disabled />
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                {/* ── BILL FROM + DISPATCH FROM ───────────────────────────── */}
                <Grid container spacing={1.5} sx={{ mb: 1.5, width: '100%', m: 0 }}>
                    <Grid item xs={12} sm={6} sx={{ pl: '0 !important', pr: '6px' }}>
                        <Paper elevation={1} sx={{ borderRadius: '4px', overflow: 'hidden', height: '100%' }}>
                            <Box sx={sectionHeaderStyle}>Bill From</Box>
                            <Box sx={{ p: 1 }}>
                                <Grid container>
                                    <Grid item xs={12} sx={fieldWrapper}>
                                        <label style={labelStyle}>Name</label>
                                        <input style={inputSmallStyle} value={billFrom.name} disabled />
                                    </Grid>
                                    <Grid item xs={12} sm={6} sx={fieldWrapper}>
                                        <label style={labelStyle}>GSTIN {requiredStar}</label>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <input style={{ ...inputSmallStyle, flex: 1 }} value={billFrom.gstin} disabled />
                                            <Tooltip title="GSTIN info">
                                                <InfoOutlinedIcon sx={{ fontSize: '17px', color: '#888' }} />
                                            </Tooltip>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} sx={fieldWrapper}>
                                        <label style={labelStyle}>State {requiredStar}</label>
                                        <input style={inputSmallStyle} value={billFrom.state} disabled />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ pr: '0 !important', pl: '6px' }}>
                        <Paper elevation={1} sx={{ borderRadius: '4px', overflow: 'hidden', height: '100%' }}>
                            <Box sx={sectionHeaderStyle}>Dispatch From</Box>
                            <Box sx={{ p: 1 }}>
                                <Grid container>
                                    <Grid item xs={12} sx={fieldWrapper}>
                                        <label style={labelStyle}>Address</label>
                                        <input style={inputSmallStyle} value={dispatchFrom.address} disabled />
                                    </Grid>
                                    <Grid item xs={12} sm={4} sx={fieldWrapper}>
                                        <label style={labelStyle}>Place</label>
                                        <input style={inputSmallStyle} value={dispatchFrom.place} disabled />
                                    </Grid>
                                    <Grid item xs={12} sm={4} sx={fieldWrapper}>
                                        <label style={labelStyle}>Pincode {requiredStar}</label>
                                        <input style={inputSmallStyle} value={dispatchFrom.pincode} disabled />
                                    </Grid>
                                    <Grid item xs={12} sm={4} sx={fieldWrapper}>
                                        <label style={labelStyle}>State</label>
                                        <input style={inputSmallStyle} value={dispatchFrom.state} disabled />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {/* ── BILL TO + SHIP TO ────────────────────────────────────── */}
                <Grid container spacing={1.5} sx={{ mb: 1.5, width: '100%', m: 0, mt: 1.5 }}>
                    <Grid item xs={12} sm={6} sx={{ pl: '0 !important', pr: '6px' }}>
                        <Paper elevation={1} sx={{ borderRadius: '4px', overflow: 'hidden', height: '100%' }}>
                            <Box sx={sectionHeaderStyle}>Bill To</Box>
                            <Box sx={{ p: 1 }}>
                                <Grid container>
                                    <Grid item xs={12} sx={fieldWrapper}>
                                        <label style={labelStyle}>Name</label>
                                        <input style={inputSmallStyle} value={billTo.name} disabled />
                                    </Grid>
                                    <Grid item xs={12} sm={6} sx={fieldWrapper}>
                                        <label style={labelStyle}>GSTIN {requiredStar}</label>
                                        <input style={inputSmallStyle} value={billTo.gstin} disabled />
                                    </Grid>
                                    <Grid item xs={12} sm={6} sx={fieldWrapper}>
                                        <label style={labelStyle}>State {requiredStar}</label>
                                        <input style={inputSmallStyle} value={billTo.state} disabled />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ pr: '0 !important', pl: '6px' }}>
                        <Paper elevation={1} sx={{ borderRadius: '4px', overflow: 'hidden', height: '100%' }}>
                            <Box sx={sectionHeaderStyle}>Ship To</Box>
                            <Box sx={{ p: 1 }}>
                                <Grid container>
                                    <Grid item xs={12} sx={fieldWrapper}>
                                        <label style={labelStyle}>Address</label>
                                        <input style={inputSmallStyle} value={shipTo.address} disabled />
                                    </Grid>
                                    <Grid item xs={12} sm={4} sx={fieldWrapper}>
                                        <label style={labelStyle}>Place</label>
                                        <input style={inputSmallStyle} value={shipTo.place} disabled />
                                    </Grid>
                                    <Grid item xs={12} sm={4} sx={fieldWrapper}>
                                        <label style={labelStyle}>Pincode {requiredStar}</label>
                                        <input style={inputSmallStyle} value={shipTo.pincode} disabled />
                                    </Grid>
                                    <Grid item xs={12} sm={4} sx={fieldWrapper}>
                                        <label style={labelStyle}>State</label>
                                        <input style={inputSmallStyle} value={shipTo.state} disabled />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {/* ── ITEM DETAILS ─────────────────────────────────────────── */}
                <Paper elevation={1} sx={{ mb: 1.5, borderRadius: '4px', overflow: 'hidden', width: '100%', mt: 1.5 }}>
                    <Box sx={{ ...sectionHeaderStyle, mb: 0 }}>Item Details</Box>
                    <Box sx={{ overflowX: 'auto', width: '100%' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
                            <thead>
                                <tr>
                                    <th style={tableCellHeaderStyle}>Product Name</th>
                                    <th style={tableCellHeaderStyle}>Description</th>
                                    <th style={tableCellHeaderStyle}>HSN</th>
                                    <th style={tableCellHeaderStyle}>Quantity</th>
                                    <th style={tableCellHeaderStyle}>Unit</th>
                                    <th style={tableCellHeaderStyle}>Value/Taxable Value (Rs.)</th>
                                    <th style={tableCellHeaderStyle}>CGST+SGST Rate(%)</th>
                                    <th style={tableCellHeaderStyle}>IGST Rate(%)</th>
                                    <th style={tableCellHeaderStyle}>CESS Advol Rate(%)</th>
                                    <th style={tableCellHeaderStyle}>CESS non Advol. Rate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, idx) => (
                                    <tr key={item.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                                        <td style={tableCellStyle}>{item.product_name || '—'}</td>
                                        <td style={tableCellStyle}>{item.description || '—'}</td>
                                        <td style={tableCellStyle}>{item.hsn || '0'}</td>
                                        <td style={tableCellStyle}>{item.quantity ? parseFloat(item.quantity).toFixed(2) : '0.00'}</td>
                                        <td style={tableCellStyle}>{item.unit || '—'}</td>
                                        <td style={tableCellStyle}>{item.value_taxable_value ? parseFloat(item.value_taxable_value).toFixed(2) : '0.00'}</td>
                                        <td style={tableCellStyle}>{item.cgst_sgst_rate ? parseFloat(item.cgst_sgst_rate).toFixed(2) : '0.00'}</td>
                                        <td style={tableCellStyle}>{item.igst_rate ? parseFloat(item.igst_rate).toFixed(2) : '0.00'}</td>
                                        <td style={tableCellStyle}>{item.cess_adv_rate ? parseFloat(item.cess_adv_rate).toFixed(2) : '0.00'}</td>
                                        <td style={tableCellStyle}>{item.cess_non_adv_rate ? parseFloat(item.cess_non_adv_rate).toFixed(2) : '0.00'}</td>
                                    </tr>
                                ))}
                                {/* ── Totals Row ── */}
                                <tr style={{ backgroundColor: '#e8edf2', borderTop: '2px solid #607d8b' }}>
                                    <td colSpan={10} style={{ padding: '10px 14px', backgroundColor: '#e8edf2', borderTop: '2px solid #607d8b' }}>
                                        <Grid container spacing={1} alignItems="center">
                                            <Grid item xs={12} sm={1.5}>
                                                <label style={{ fontSize: '1rem', fontWeight: 700, color: '#2c3e50', display: 'block', marginBottom: '4px' }}>Total Tax.Amt {requiredStar}</label>
                                                <input style={{ ...inputSmallStyle, height: '38px', fontSize: '0.9rem' }} value={totals.total_taxable_amount ? parseFloat(totals.total_taxable_amount).toFixed(2) : '0.00'} readOnly />
                                            </Grid>
                                            <Grid item xs={12} sm={1.5}>
                                                <label style={{ fontSize: '1rem', fontWeight: 700, color: '#2c3e50', display: 'block', marginBottom: '4px' }}>CGST Amt {requiredStar}</label>
                                                <input style={{ ...inputSmallStyle, height: '38px', fontSize: '0.9rem' }} value={totals.cgst_amount ? parseFloat(totals.cgst_amount).toFixed(2) : '0.00'} readOnly />
                                            </Grid>
                                            <Grid item xs={12} sm={1.5}>
                                                <label style={{ fontSize: '1rem', fontWeight: 700, color: '#2c3e50', display: 'block', marginBottom: '4px' }}>SGST Amt {requiredStar}</label>
                                                <input style={{ ...inputSmallStyle, height: '38px', fontSize: '0.9rem' }} value={totals.sgst_amount ? parseFloat(totals.sgst_amount).toFixed(2) : '0.00'} readOnly />
                                            </Grid>
                                            <Grid item xs={12} sm={1.5}>
                                                <label style={{ fontSize: '1rem', fontWeight: 700, color: '#2c3e50', display: 'block', marginBottom: '4px' }}>IGST Amt {requiredStar}</label>
                                                <input style={{ ...inputSmallStyle, height: '38px', fontSize: '0.9rem' }} value={totals.igst_amount ? parseFloat(totals.igst_amount).toFixed(2) : '0.00'} readOnly />
                                            </Grid>
                                            <Grid item xs={12} sm={1.5}>
                                                <label style={{ fontSize: '1rem', fontWeight: 700, color: '#2c3e50', display: 'block', marginBottom: '4px' }}>Cess Adv Amt {requiredStar}</label>
                                                <input style={{ ...inputSmallStyle, height: '38px', fontSize: '0.9rem' }} value={totals.cess_adv_amount ? parseFloat(totals.cess_adv_amount).toFixed(2) : '0.00'} readOnly />
                                            </Grid>
                                            <Grid item xs={12} sm={1.5}>
                                                <label style={{ fontSize: '1rem', fontWeight: 700, color: '#2c3e50', display: 'block', marginBottom: '4px' }}>Cess Non Adv {requiredStar}</label>
                                                <input style={{ ...inputSmallStyle, height: '38px', fontSize: '0.9rem' }} value={totals.cess_non_adv_amount ? parseFloat(totals.cess_non_adv_amount).toFixed(2) : '0.00'} readOnly />
                                            </Grid>
                                            <Grid item xs={12} sm={1.5}>
                                                <label style={{ fontSize: '1rem', fontWeight: 700, color: '#2c3e50', display: 'block', marginBottom: '4px' }}>Other Amt(+)</label>
                                                <input style={{ ...inputSmallStyle, height: '38px', fontSize: '0.9rem' }} value={totals.other_amount ? parseFloat(totals.other_amount).toFixed(2) : '0.00'} readOnly />
                                            </Grid>
                                            <Grid item xs={12} sm={1.5}>
                                                <label style={{ fontSize: '1rem', fontWeight: 700, color: '#2c3e50', display: 'block', marginBottom: '4px' }}>Total Inv. Amt</label>
                                                <input style={{ ...inputSmallStyle, height: '38px', fontSize: '1rem' }} value={totals.total_inv_amount ? parseFloat(totals.total_inv_amount).toFixed(2) : '0.00'} readOnly />
                                            </Grid>
                                        </Grid>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </Box>
                </Paper>

                {/* ── TRANSPORTATION DETAILS ───────────────────────────────── */}
                <Paper elevation={1} sx={{ mb: 1.5, borderRadius: '4px', overflow: 'hidden', width: '100%', mt: 1.5 }}>
                    <Box sx={sectionHeaderStyle}>Transportation Details</Box>
                    <Box sx={{ p: '4px 16px' }}>
                        <Grid container sx={{ mt: 0 }}>
                            <Grid item xs={12} sm={3} sx={fieldWrapper}>
                                <label style={labelStyle}>Transporter ID {requiredStar}</label>

                                <input
                                    style={{
                                        ...inputSmallStyle,
                                        borderColor: transporterIdError ? 'red' : '#ccc'
                                    }}
                                    placeholder="Transporter GSTIN"
                                    value={transporterId}
                                    disabled={ewayBillExists}
                                    maxLength={15}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                                        setTransporterId(val);
                                        const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
                                        if (val && !gstinRegex.test(val)) {
                                            setTransporterIdError('Invalid GSTIN format (e.g. 29ABCDE1234F1Z5)');
                                        } else {
                                            setTransporterIdError('');
                                        }
                                    }}
                                />
                                {transporterIdError && (
                                    <span style={{ color: 'red', fontSize: '0.8rem', marginTop: '3px', display: 'block' }}>
                                        {transporterIdError}
                                    </span>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={3} sx={fieldWrapper}>
                                <label style={labelStyle}>Transporter Name {requiredStar}</label>
                                <input
                                    style={inputSmallStyle}
                                    placeholder="Transporter Name"
                                    value={transporterName}
                                    disabled={ewayBillExists}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                                        setTransporterName(val);
                                        if (val.trim()) setTransporterNameError('');
                                    }}
                                />
                                {transporterNameError && (
                                    <span style={{ color: 'red', fontSize: '0.8rem', marginTop: '3px', display: 'block' }}>
                                        {transporterNameError}
                                    </span>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={3} sx={fieldWrapper}>
                                <label style={labelStyle}>Auto Calculated PIN to PIN (in KM) {requiredStar}</label>
                                <input
                                    style={inputSmallStyle}
                                    placeholder="Auto Calculated (KM)"
                                    value={autoCalculatedDistance}
                                    disabled={ewayBillExists}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                                        setAutoCalculatedDistance(val);
                                        if (val) setAutoCalculatedDistanceError('');
                                    }}
                                />
                                {/* ADD this: */}
                                {autoCalculatedDistanceError && (
                                    <span style={{ color: 'red', fontSize: '0.8rem', marginTop: '3px', display: 'block' }}>
                                        {autoCalculatedDistanceError}
                                    </span>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={3} sx={fieldWrapper}>
                                <label style={labelStyle}>Approximate Distance (in KM) {requiredStar}</label>
                                <input
                                    style={inputSmallStyle}
                                    placeholder="Enter Distance (KM)"
                                    value={approximateDistance}
                                    disabled={ewayBillExists}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                                        setApproximateDistance(val);
                                        // ADD: clear error on change
                                        if (val) setApproximateDistanceError('');
                                    }}
                                />

                                {approximateDistanceError && (
                                    <span style={{ color: 'red', fontSize: '0.8rem', marginTop: '3px', display: 'block' }}>
                                        {approximateDistanceError}
                                    </span>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                {/* ── PART-B ───────────────────────────────────────────────── */}
                <Paper elevation={1} sx={{ mb: 1.5, borderRadius: '4px', overflow: 'hidden', width: '100%', mt: 1.5 }}>
                    <Box sx={sectionHeaderStyle}>Part-B</Box>
                    <Box sx={{ p: '8px 16px' }}>
                        <style>{`
                            input[type="date"].big-date::-webkit-calendar-picker-indicator {
                                width: 22px; height: 22px; cursor: pointer; opacity: 0.7;
                            }
                            input[type="date"].big-date { font-size: 1rem; min-width: 180px; }
                        `}</style>
                        <Grid container alignItems="flex-start" sx={{ mt: 0 }}>
                            <Grid item xs={12} sm={6} sx={fieldWrapper}>
                                <label style={labelStyle}>Mode</label>
                                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'nowrap', alignItems: 'center' }}>
                                    {['Road', 'Rail', 'Air', 'Ship or Ship Cum Road/Rail', 'inTransit'].map((v) => (
                                        <label key={v} style={{
                                            fontSize: '1.1rem', display: 'flex', alignItems: 'center',
                                            gap: '6px', cursor: ewayBillExists ? 'default' : 'pointer', whiteSpace: 'nowrap'
                                        }}>
                                            <input
                                                type="radio" name="transport_mode" value={v}
                                                checked={transportMode === v}
                                                onChange={() => !ewayBillExists && setTransportMode(v)}
                                                disabled={ewayBillExists}
                                                style={{ accentColor: '#607d8b', width: '16px', height: '16px' }}
                                            />
                                            {v}
                                        </label>
                                    ))}
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} sx={fieldWrapper}>
                                <label style={labelStyle}>Vehicle Type</label>
                                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'nowrap', alignItems: 'center' }}>
                                    {['Regular', 'Over Dimensional Cargo'].map((v) => (
                                        <label key={v} style={{
                                            fontSize: '1.1rem', display: 'flex', alignItems: 'center',
                                            gap: '6px', cursor: ewayBillExists ? 'default' : 'pointer', whiteSpace: 'nowrap'
                                        }}>
                                            <input
                                                type="radio" name="vehicle_type" value={v}
                                                checked={vehicleType === v}
                                                onChange={() => !ewayBillExists && setVehicleType(v)}
                                                disabled={ewayBillExists}
                                                style={{ accentColor: '#607d8b', width: '16px', height: '16px' }}
                                            />
                                            {v}
                                        </label>
                                    ))}
                                </Box>
                            </Grid>
                        </Grid>
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px', marginBottom: '8px' }}>
                            <tbody>
                                <tr>
                                    <td style={{ border: '1px solid #ccc', padding: '10px 14px', width: '50%', verticalAlign: 'middle' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <label style={{ ...labelStyle, marginBottom: 0, whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
                                                Vehicle No.
                                            </label>
                                            <Tooltip title="Vehicle number">
                                                <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#888', flexShrink: 0 }} />
                                            </Tooltip>
                                            <input
                                                style={{ ...inputSmallStyle, flex: 1, marginLeft: '8px' }}
                                                value={vehicleNo}
                                                disabled={ewayBillExists}
                                                onChange={(e) => {
                                                    const val = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                                                    setVehicleNo(val);
                                                }}
                                                placeholder="Vehicle No."
                                            />
                                        </Box>
                                    </td>
                                    <td style={{ border: '1px solid #ccc', padding: '10px 14px', width: '50%', verticalAlign: 'middle' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <label style={{ ...labelStyle, marginBottom: 0, whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
                                                Transporter Doc. No. &amp; Date
                                            </label>
                                            <input
                                                style={{ ...inputSmallStyle, flex: 1, marginLeft: '8px' }}
                                                value={transporterDocNo}
                                                disabled={ewayBillExists}
                                                onChange={(e) => {
                                                    const val = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                                                    setTransporterDocNo(val);
                                                }}
                                                placeholder="Doc No."
                                            />
                                            <input
                                                type="date"
                                                className="big-date"
                                                style={{ ...inputSmallStyle, flex: 1 }}
                                                value={transporterDocDate
                                                    ? moment(transporterDocDate, ['DD/MM/YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD')
                                                    : ''}
                                                disabled={ewayBillExists}
                                                onChange={(e) =>
                                                    setTransporterDocDate(
                                                        e.target.value
                                                            ? moment(e.target.value, 'YYYY-MM-DD').format('DD/MM/YYYY')
                                                            : ''
                                                    )
                                                }
                                            />
                                        </Box>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </Box>
                </Paper>

                {/* ── PREVIEW / SUBMIT / EXIT BUTTONS ─────────────────────── */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, mb: 1 }}>
                    <Button
                        variant="contained" size="small"
                        onClick={() => setPreviewOpen(true)}
                        sx={{
                            textTransform: 'none', fontSize: '1rem', fontWeight: 700,
                            backgroundColor: '#f5a623', '&:hover': { backgroundColor: '#e09410' },
                            minWidth: '100px'
                        }}
                    >
                        Preview
                    </Button>
                    {!ewayBillExists && (
                        <Button
                            type="submit" variant="contained" size="small"
                            disabled={submitLoading}
                            sx={{
                                textTransform: 'none', fontSize: '1rem', fontWeight: 700,
                                backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' },
                                minWidth: '100px'
                            }}
                        >
                            {submitLoading ? 'Saving...' : 'Submit'}
                        </Button>
                    )}
                    <Button
                        variant="contained" size="small" onClick={handleBack}
                        sx={{
                            textTransform: 'none', fontSize: '1rem', fontWeight: 700,
                            backgroundColor: '#e57373', '&:hover': { backgroundColor: '#c62828' },
                            minWidth: '100px'
                        }}
                    >
                        Exit
                    </Button>
                </Box>

                {/* ── PREVIEW DIALOG ───────────────────────────────────────── */}
                <Dialog
                    open={previewOpen} onClose={() => setPreviewOpen(false)}
                    maxWidth="lg" fullWidth
                    PaperProps={{ sx: { borderRadius: '6px', fontFamily: 'Dosis' } }}
                >
                    <DialogTitle sx={{
                        backgroundColor: '#d3dae0', borderLeft: '4px solid #607d8b',
                        fontWeight: 700, fontSize: '1.2rem', color: '#2c3e50',
                        fontFamily: 'Dosis', py: 1.5
                    }}>
                        E-Way Bill Preview
                    </DialogTitle>
                    <DialogContent dividers sx={{ backgroundColor: '#f0f2f5', p: 2 }}>

                        {/* Bill From + Dispatch From */}
                        <Grid container spacing={1.5} sx={{ mb: 2, m: 0, width: '100%' }}>
                            <Grid item xs={12} sm={6} sx={{ pl: '0 !important', pr: '6px' }}>
                                <Paper elevation={1} sx={{ borderRadius: '4px', overflow: 'hidden', height: '100%' }}>
                                    <Box sx={sectionHeaderStyle}>Bill From</Box>
                                    <Box sx={{ p: 1 }}>
                                        <Grid container>
                                            {[
                                                { label: 'Name', value: billFrom.name },
                                                { label: 'GSTIN', value: billFrom.gstin },
                                                { label: 'State', value: billFrom.state },
                                            ].map((f) => (
                                                <Grid item xs={12} sm={f.label === 'Name' ? 12 : 6} sx={fieldWrapper} key={f.label}>
                                                    <label style={labelStyle}>{f.label}</label>
                                                    <input style={inputSmallStyle} value={f.value} readOnly />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ pr: '0 !important', pl: '6px' }}>
                                <Paper elevation={1} sx={{ borderRadius: '4px', overflow: 'hidden', height: '100%' }}>
                                    <Box sx={sectionHeaderStyle}>Dispatch From</Box>
                                    <Box sx={{ p: 1 }}>
                                        <Grid container>
                                            {[
                                                { label: 'Address', value: dispatchFrom.address, sm: 12 },
                                                { label: 'Place', value: dispatchFrom.place, sm: 4 },
                                                { label: 'Pincode', value: dispatchFrom.pincode, sm: 4 },
                                                { label: 'State', value: dispatchFrom.state, sm: 4 },
                                            ].map((f) => (
                                                <Grid item xs={12} sm={f.sm} sx={fieldWrapper} key={f.label}>
                                                    <label style={labelStyle}>{f.label}</label>
                                                    <input style={inputSmallStyle} value={f.value} readOnly />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Bill To + Ship To */}
                        <Grid container spacing={1.5} sx={{ mb: 2, m: 0, mt: 1.5, width: '100%' }}>
                            <Grid item xs={12} sm={6} sx={{ pl: '0 !important', pr: '6px' }}>
                                <Paper elevation={1} sx={{ borderRadius: '4px', overflow: 'hidden', height: '100%' }}>
                                    <Box sx={sectionHeaderStyle}>Bill To</Box>
                                    <Box sx={{ p: 1 }}>
                                        <Grid container>
                                            {[
                                                { label: 'Name', value: billTo.name, sm: 12 },
                                                { label: 'GSTIN', value: billTo.gstin, sm: 6 },
                                                { label: 'State', value: billTo.state, sm: 6 },
                                            ].map((f) => (
                                                <Grid item xs={12} sm={f.sm} sx={fieldWrapper} key={f.label}>
                                                    <label style={labelStyle}>{f.label}</label>
                                                    <input style={inputSmallStyle} value={f.value} readOnly />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ pr: '0 !important', pl: '6px' }}>
                                <Paper elevation={1} sx={{ borderRadius: '4px', overflow: 'hidden', height: '100%' }}>
                                    <Box sx={sectionHeaderStyle}>Ship To</Box>
                                    <Box sx={{ p: 1 }}>
                                        <Grid container>
                                            {[
                                                { label: 'Address', value: shipTo.address, sm: 12 },
                                                { label: 'Place', value: shipTo.place, sm: 4 },
                                                { label: 'Pincode', value: shipTo.pincode, sm: 4 },
                                                { label: 'State', value: shipTo.state, sm: 4 },
                                            ].map((f) => (
                                                <Grid item xs={12} sm={f.sm} sx={fieldWrapper} key={f.label}>
                                                    <label style={labelStyle}>{f.label}</label>
                                                    <input style={inputSmallStyle} value={f.value} readOnly />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Item Details */}
                        <Paper elevation={1} sx={{ mb: 2, borderRadius: '4px', overflow: 'hidden', mt: 1.5 }}>
                            <Box sx={{ ...sectionHeaderStyle, mb: 0 }}>Item Details</Box>
                            <Box sx={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
                                    <thead>
                                        <tr>
                                            {['Product Name', 'Description', 'HSN', 'Quantity', 'Unit',
                                                'Value/Taxable Value (Rs.)', 'CGST+SGST Rate(%)', 'IGST Rate(%)',
                                                'CESS Advol Rate(%)', 'CESS non Advol. Rate'].map((h) => (
                                                    <th key={h} style={tableCellHeaderStyle}>{h}</th>
                                                ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((item, idx) => (
                                            <tr key={item.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                                                <td style={tableCellStyle}>{item.product_name}</td>
                                                <td style={tableCellStyle}>{item.description}</td>
                                                <td style={tableCellStyle}>{item.hsn}</td>
                                                <td style={tableCellStyle}>{item.quantity}</td>
                                                <td style={tableCellStyle}>{item.unit}</td>
                                                <td style={tableCellStyle}>{item.value_taxable_value}</td>
                                                <td style={tableCellStyle}>{item.cgst_sgst_rate}</td>
                                                <td style={tableCellStyle}>{item.igst_rate}</td>
                                                <td style={tableCellStyle}>{item.cess_adv_rate}</td>
                                                <td style={tableCellStyle}>{item.cess_non_adv_rate}</td>
                                            </tr>
                                        ))}
                                        <tr style={{ backgroundColor: '#e8edf2', borderTop: '2px solid #607d8b' }}>
                                            <td colSpan={10} style={{ padding: '10px 14px' }}>
                                                <Grid container spacing={1} alignItems="center">
                                                    {[
                                                        { label: 'Total Tax.Amt', value: totals.total_taxable_amount },
                                                        { label: 'CGST Amt', value: totals.cgst_amount },
                                                        { label: 'SGST Amt', value: totals.sgst_amount },
                                                        { label: 'IGST Amt', value: totals.igst_amount },
                                                        { label: 'Cess Adv Amt', value: totals.cess_adv_amount },
                                                        { label: 'Cess Non Adv', value: totals.cess_non_adv_amount },
                                                        { label: 'Other Amt(+)', value: totals.other_amount },
                                                        { label: 'Total Inv. Amt', value: totals.total_inv_amount },
                                                    ].map((f) => (
                                                        <Grid item xs={12} sm={1.5} key={f.label}>
                                                            <label style={{ fontSize: '1rem', fontWeight: 700, color: '#2c3e50', display: 'block', marginBottom: '4px', fontFamily: 'Dosis' }}>{f.label}</label>
                                                            <input style={{ ...inputSmallStyle, height: '38px', fontSize: '0.9rem' }} value={f.value} readOnly />
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </Box>
                        </Paper>

                        {/* Transportation Details */}
                        <Paper elevation={1} sx={{ mb: 1.5, borderRadius: '4px', overflow: 'hidden', width: '100%', mt: 1.5 }}>
                            <Box sx={sectionHeaderStyle}>Transportation Details</Box>
                            <Box sx={{ p: '4px 16px' }}>
                                <Grid container sx={{ mt: 0 }}>
                                    <Grid item xs={12} sm={3} sx={fieldWrapper}>
                                        <label style={labelStyle}>Transporter ID {requiredStar}</label>
                                        <input style={inputSmallStyle} value={transporterId} readOnly />
                                    </Grid>
                                    <Grid item xs={12} sm={3} sx={fieldWrapper}>
                                        <label style={labelStyle}>Transporter Name {requiredStar}</label>
                                        <input style={inputSmallStyle} value={transporterName} readOnly />
                                    </Grid>
                                    <Grid item xs={12} sm={3} sx={fieldWrapper}>
                                        <label style={labelStyle}>Auto Calculated PIN to PIN (in KM) {requiredStar}</label>
                                        <input style={inputSmallStyle} value={autoCalculatedDistance} readOnly />
                                    </Grid>
                                    <Grid item xs={12} sm={3} sx={fieldWrapper}>
                                        <label style={labelStyle}>Approximate Distance (in KM) {requiredStar}</label>
                                        <input style={inputSmallStyle} value={approximateDistance} readOnly />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>

                        {/* Part-B Summary */}
                        <Paper elevation={1} sx={{ mb: 1, borderRadius: '4px', overflow: 'hidden', mt: 1.5 }}>
                            <Box sx={sectionHeaderStyle}>Part-B</Box>
                            <Box sx={{ p: '8px 16px 14px' }}>
                                <Grid container>
                                    {[
                                        { label: 'Mode', value: transportMode },
                                        { label: 'Vehicle Type', value: vehicleType },
                                        { label: 'Vehicle No.', value: vehicleNo },
                                        { label: 'Transporter Doc. No.', value: transporterDocNo },
                                        { label: 'Transporter Date', value: transporterDocDate },
                                    ].map((f) => (
                                        <Grid item xs={12} sm={2.4} sx={fieldWrapper} key={f.label}>
                                            <label style={{ ...labelStyle, fontSize: '0.95rem', color: '#607d8b', marginBottom: '5px' }}>{f.label}</label>
                                            <Box sx={{
                                                height: '42px', display: 'flex', alignItems: 'center',
                                                px: '12px', borderRadius: '4px', border: '1.5px solid #b0bec5',
                                                backgroundColor: '#fff', fontSize: '1rem',
                                                fontFamily: 'Dosis', fontWeight: 600, color: '#2c3e50',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                                            }}>
                                                {f.value || <span style={{ color: '#bbb', fontWeight: 400 }}>—</span>}
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </Paper>

                    </DialogContent>
                    <DialogActions sx={{ backgroundColor: '#f0f2f5', px: 3, py: 1.5 }}>
                        <Button
                            onClick={() => setPreviewOpen(false)}
                            variant="contained" size="small"
                            sx={{
                                textTransform: 'none', fontSize: '1rem', fontWeight: 700,
                                backgroundColor: '#e57373', '&:hover': { backgroundColor: '#c62828' },
                                minWidth: '100px', fontFamily: 'Dosis'
                            }}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

            </ValidatorForm>

            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                open={snackbarOpen} autoHideDuration={4500} onClose={handleSnackbarClose}
            >
                <MuiAlert elevation={6} variant="filled" onClose={handleSnackbarClose} severity={snackbarSeverity}>
                    {responseMessage}
                </MuiAlert>
            </Snackbar>
        </Box>
    );
};

export default EWayBill;